import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Save, Star } from 'lucide-react';

/**
 * QuickFilterChips - Reusable filter chips component
 * Features:
 * - Clickable filter chips with counts
 * - Active/inactive states
 * - "All" option to clear filters
 * - Support for saved filters
 */
function QuickFilterChips({
  filters,
  activeFilters = [],
  onFilterChange,
  savedFilters = [],
  onSaveFilter,
  onLoadFilter,
  onDeleteSavedFilter,
  showSaved = false,
  multiSelect = false,
  className = '',
  totalCount = null  // Optional: total unique count (avoids double-counting overlapping filters)
}) {
  const { t } = useTranslation();
  const handleChipClick = (filterKey) => {
    if (filterKey === 'all') {
      onFilterChange([]);
      return;
    }

    if (multiSelect) {
      if (activeFilters.includes(filterKey)) {
        onFilterChange(activeFilters.filter(f => f !== filterKey));
      } else {
        onFilterChange([...activeFilters, filterKey]);
      }
    } else {
      if (activeFilters.includes(filterKey)) {
        onFilterChange([]);
      } else {
        onFilterChange([filterKey]);
      }
    }
  };

  const isAllActive = activeFilters.length === 0;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Main Filter Chips */}
      <div className="flex flex-wrap gap-2">
        {/* All/Clear Chip */}
        <button
          onClick={() => handleChipClick('all')}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
            isAllActive
              ? 'bg-amber-500 text-white shadow-md'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {t('common.table.allChip')}
          {(() => {
            // Use totalCount if provided (avoids double-counting overlapping filters like "issues")
            // Otherwise fall back to sum of filter counts
            const displayCount = totalCount !== null
              ? totalCount
              : filters.reduce((sum, f) => sum + (f.count || 0), 0);
            return displayCount > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                isAllActive ? 'bg-amber-600' : 'bg-gray-200'
              }`}>
                {displayCount}
              </span>
            );
          })()}
        </button>

        {/* Filter Chips */}
        {filters.map(filter => {
          const isActive = activeFilters.includes(filter.key);
          return (
            <button
              key={filter.key}
              onClick={() => handleChipClick(filter.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                isActive
                  ? `${filter.activeColor || 'bg-amber-500 text-white'} shadow-md`
                  : `${filter.inactiveColor || 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`
              }`}
              title={filter.tooltip}
            >
              {filter.icon && <filter.icon className="w-3.5 h-3.5" />}
              {filter.label}
              {filter.count !== undefined && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  isActive
                    ? filter.activeBadgeColor || 'bg-white/20'
                    : filter.inactiveBadgeColor || 'bg-gray-200'
                }`}>
                  {filter.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Saved Filters Section */}
      {showSaved && savedFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Star className="w-3 h-3" /> {t('common.table.savedFilters')}
          </span>
          {savedFilters.map(saved => (
            <div key={saved.id} className="flex items-center gap-1">
              <button
                onClick={() => onLoadFilter && onLoadFilter(saved)}
                className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
              >
                {saved.name}
              </button>
              {onDeleteSavedFilter && (
                <button
                  onClick={() => onDeleteSavedFilter(saved.id)}
                  className="p-0.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Save Current Filter Button */}
      {showSaved && activeFilters.length > 0 && onSaveFilter && (
        <button
          onClick={() => onSaveFilter(activeFilters)}
          className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <Save className="w-3 h-3" /> {t('common.table.saveFilter')}
        </button>
      )}
    </div>
  );
}

export default QuickFilterChips;
